import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, Copy, X } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Spinner } from '@/components/ui/Spinner';
import { Select } from '@/components/ui/Select';
import { useCreateOrganization } from '@/api/organizations';
import { useApplyFolderTemplate, useFolderTemplates } from '@/api/adminTemplates';
import { useInviteUser } from '@/api/users';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { FolderTemplate, FolderTemplateNode, Role } from '@/types/api';

type WizardStep = 1 | 2 | 3 | 4;

type InviteRole = Extract<Role, 'client_admin' | 'client_user'>;

interface WizardState {
  orgId: string | null;
  workspaceId: string | null;
  orgName: string;
  orgSlug: string;
  selectedTemplateId: string | null;
  defaultTemplateId: string | null;
  inviteEmail: string;
  inviteName: string;
  inviteRole: InviteRole;
  inviteToken: string | null;
  inviteSent: boolean;
  appliedTemplateName: string | null;
}

const INITIAL_STATE: WizardState = {
  orgId: null,
  workspaceId: null,
  orgName: '',
  orgSlug: '',
  selectedTemplateId: null,
  defaultTemplateId: null,
  inviteEmail: '',
  inviteName: '',
  inviteRole: 'client_admin',
  inviteToken: null,
  inviteSent: false,
  appliedTemplateName: null,
};

const STEP_LABELS: Record<WizardStep, string> = {
  1: 'Organization',
  2: 'Template',
  3: 'Primary contact',
  4: 'Done',
};

const SLUG_REGEX = /^[a-z0-9][a-z0-9-]*$/;

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function OnboardClientPage() {
  usePageTitle('Onboard client');
  const navigate = useNavigate();
  const toast = useToast();

  const [step, setStep] = useState<WizardStep>(1);
  const [state, setState] = useState<WizardState>(INITIAL_STATE);
  const [slugEdited, setSlugEdited] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const createOrg = useCreateOrganization();
  const templates = useFolderTemplates();
  const applyTemplate = useApplyFolderTemplate();
  const inviteUser = useInviteUser();

  useEffect(() => {
    const list = templates.data ?? [];
    if (list.length === 0) return;
    const def = list.find((t) => t.is_default) ?? list[0];
    setState((s) => {
      if (s.defaultTemplateId && s.selectedTemplateId) return s;
      return {
        ...s,
        defaultTemplateId: def?.id ?? null,
        selectedTemplateId: s.selectedTemplateId ?? def?.id ?? null,
      };
    });
  }, [templates.data]);

  useEffect(() => {
    if (!slugEdited) {
      const computed = slugify(state.orgName);
      setState((s) => (s.orgSlug === computed ? s : { ...s, orgSlug: computed }));
    }
  }, [state.orgName, slugEdited]);

  const reset = () => {
    setState(INITIAL_STATE);
    setSlugEdited(false);
    setConfirming(false);
    setStep(1);
  };

  const handleCancel = () => {
    navigate('/admin/organizations');
  };

  const step1Valid =
    state.orgName.trim().length > 0 && SLUG_REGEX.test(state.orgSlug.trim());

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.inviteEmail.trim());

  // Steps 1–3 only collect data; no API calls happen until Confirm on step 4.
  const handleSubmitStep1 = () => {
    if (!step1Valid) return;
    setStep(2);
  };

  const handleSubmitStep2 = (skip: boolean) => {
    const list = templates.data ?? [];
    const selected = list.find((t) => t.id === state.selectedTemplateId) ?? null;
    if (!skip && selected && !selected.is_default) {
      setState((s) => ({ ...s, selectedTemplateId: selected.id }));
    }
    setStep(3);
  };

  const handleSubmitStep3 = (skip: boolean) => {
    if (!skip && state.inviteEmail.trim() && !emailValid) {
      toast.error('Invalid email');
      return;
    }
    if (skip) {
      setState((s) => ({ ...s, inviteEmail: '', inviteName: '' }));
    }
    setStep(4);
  };

  // Step 4: create org, apply template, send invite — all at once.
  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const org = await createOrg.mutateAsync({
        name: state.orgName.trim(),
        slug: state.orgSlug.trim(),
        // Pre-fill primary contact email with the first invite's email if provided
        ...(state.inviteEmail.trim()
          ? { primary_contact_email: state.inviteEmail.trim() }
          : {}),
      });

      let appliedTemplateName: string | null = null;
      const list = templates.data ?? [];
      const selected = list.find((t) => t.id === state.selectedTemplateId) ?? null;
      if (selected && !selected.is_default && org.workspace_id) {
        try {
          await applyTemplate.mutateAsync({
            templateId: selected.id,
            workspaceId: org.workspace_id,
          });
          appliedTemplateName = selected.name;
        } catch (e) {
          toast.error('Template apply failed', apiError(e));
        }
      }

      let inviteSent = false;
      let inviteToken: string | null = null;
      if (state.inviteEmail.trim()) {
        try {
          const invite = await inviteUser.mutateAsync({
            email: state.inviteEmail.trim(),
            role: state.inviteRole,
            organization_id: org.id,
            full_name: state.inviteName.trim() || undefined,
          });
          inviteSent = true;
          inviteToken = invite.token ?? null;
        } catch (e) {
          toast.error('Invite failed', apiError(e));
        }
      }

      setState((s) => ({
        ...s,
        orgId: org.id,
        workspaceId: org.workspace_id ?? null,
        appliedTemplateName,
        inviteSent,
        inviteToken,
      }));
    } catch (e) {
      toast.error('Create failed', apiError(e));
    } finally {
      setConfirming(false);
    }
  };

  const isDone = state.orgId !== null;

  return (
    <>
      <Card
        title="Onboard new client"
        actions={
          !isDone ? (
            <Button variant="ghost" onClick={handleCancel} className="text-xs">
              <X size={14} /> Cancel
            </Button>
          ) : undefined
        }
      >
        <StepIndicator current={step} />

        <div className="mt-6">
          {step === 1 && (
            <Step1
              orgName={state.orgName}
              orgSlug={state.orgSlug}
              slugInvalid={state.orgSlug.length > 0 && !SLUG_REGEX.test(state.orgSlug)}
              onNameChange={(value) => setState((s) => ({ ...s, orgName: value }))}
              onSlugChange={(value) => {
                setSlugEdited(true);
                setState((s) => ({ ...s, orgSlug: value }));
              }}
            />
          )}

          {step === 2 && (
            <Step2
              templates={templates.data ?? []}
              loading={templates.isLoading}
              errorText={templates.isError ? apiError(templates.error) : null}
              selectedId={state.selectedTemplateId}
              defaultId={state.defaultTemplateId}
              onSelect={(id) => setState((s) => ({ ...s, selectedTemplateId: id }))}
            />
          )}

          {step === 3 && (
            <Step3
              email={state.inviteEmail}
              name={state.inviteName}
              role={state.inviteRole}
              onEmailChange={(v) => setState((s) => ({ ...s, inviteEmail: v }))}
              onNameChange={(v) => setState((s) => ({ ...s, inviteName: v }))}
              onRoleChange={(v) => setState((s) => ({ ...s, inviteRole: v }))}
            />
          )}

          {step === 4 && (
            <Step4
              done={isDone}
              orgId={state.orgId}
              orgName={state.orgName}
              orgSlug={state.orgSlug}
              selectedTemplateName={
                (templates.data ?? []).find((t) => t.id === state.selectedTemplateId)?.name ?? null
              }
              appliedTemplateName={state.appliedTemplateName}
              inviteEmail={state.inviteEmail}
              inviteSent={state.inviteSent}
              inviteToken={state.inviteToken}
              onOnboardAnother={reset}
            />
          )}
        </div>

        {!isDone && (
          <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
            <div className="flex items-center gap-2">
              {step > 1 && (
                <Button
                  variant="secondary"
                  onClick={() => setStep((s) => (s > 1 ? ((s - 1) as WizardStep) : s))}
                  disabled={confirming}
                  className="text-xs"
                >
                  <ChevronLeft size={14} /> Back
                </Button>
              )}
              <Button variant="ghost" onClick={handleCancel} className="text-xs" disabled={confirming}>
                Cancel
              </Button>
            </div>

            <div className="flex items-center gap-2">
              {step === 1 && (
                <Button
                  variant="primary"
                  onClick={handleSubmitStep1}
                  disabled={!step1Valid}
                  className="w-auto"
                >
                  Next
                </Button>
              )}
              {step === 2 && (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => handleSubmitStep2(true)}
                    className="text-xs"
                  >
                    Skip — use default
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleSubmitStep2(false)}
                    disabled={!state.selectedTemplateId}
                    className="w-auto"
                  >
                    Next
                  </Button>
                </>
              )}
              {step === 3 && (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => handleSubmitStep3(true)}
                    className="text-xs"
                  >
                    Skip — invite later
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleSubmitStep3(false)}
                    disabled={state.inviteEmail.trim().length > 0 && !emailValid}
                    className="w-auto"
                  >
                    Next
                  </Button>
                </>
              )}
              {step === 4 && (
                <Button
                  variant="primary"
                  onClick={handleConfirm}
                  loading={confirming}
                  disabled={confirming}
                  className="w-auto"
                >
                  Confirm &amp; create
                </Button>
              )}
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

// -------------------- Step indicator --------------------

function StepIndicator({ current }: { current: WizardStep }) {
  const steps: WizardStep[] = [1, 2, 3, 4];
  return (
    <ol className="flex items-center w-full" aria-label="Wizard progress">
      {steps.map((s, idx) => {
        const completed = s < current;
        const active = s === current;
        return (
          <li key={s} className={cn('flex items-center', idx < steps.length - 1 && 'flex-1')}>
            <div className="flex flex-col items-center">
              <div
                className={cn(
                  'w-8 h-8 flex items-center justify-center text-sm font-semibold border',
                  completed
                    ? 'bg-bb-mint text-bb-navy border-bb-mint'
                    : active
                      ? 'bg-bb-navy text-white border-bb-navy'
                      : 'bg-white text-slate-500 border-slate-300',
                )}
                aria-current={active ? 'step' : undefined}
              >
                {completed ? <Check size={16} /> : s}
              </div>
              <div
                className={cn(
                  'mt-1 text-xs',
                  active ? 'text-bb-navy font-semibold' : 'text-slate-500',
                )}
              >
                {STEP_LABELS[s]}
              </div>
            </div>
            {idx < steps.length - 1 && (
              <div
                className={cn(
                  'flex-1 h-0.5 mx-2 mb-5',
                  s < current ? 'bg-bb-mint' : 'bg-slate-200',
                )}
                aria-hidden="true"
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// -------------------- Step 1 --------------------

interface Step1Props {
  orgName: string;
  orgSlug: string;
  slugInvalid: boolean;
  onNameChange: (v: string) => void;
  onSlugChange: (v: string) => void;
}

function Step1({ orgName, orgSlug, slugInvalid, onNameChange, onSlugChange }: Step1Props) {
  return (
    <div className="max-w-lg space-y-4">
      <div>
        <h2 className="text-base font-semibold text-bb-navy">Organization</h2>
        <p className="text-xs text-slate-500 mt-1">
          Set up the client organization. Nothing is created until you confirm on the final step.
        </p>
      </div>
      <div>
        <Label htmlFor="onboard-org-name">Organization name</Label>
        <Input
          id="onboard-org-name"
          value={orgName}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Acme Corp"
          autoFocus
        />
      </div>
      <div>
        <Label htmlFor="onboard-org-slug">Slug</Label>
        <Input
          id="onboard-org-slug"
          value={orgSlug}
          onChange={(e) => onSlugChange(e.target.value)}
          placeholder="acme-corp"
          invalid={slugInvalid}
        />
        <div className="mt-1 flex flex-col gap-1">
          <span className="text-xs text-slate-500">Will appear in the URL.</span>
          {slugInvalid && (
            <span className="text-xs text-red-600">
              Slug must start with a letter or number and contain only lowercase letters, numbers
              and hyphens.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// -------------------- Step 2 --------------------

interface Step2Props {
  templates: FolderTemplate[];
  loading: boolean;
  errorText: string | null;
  selectedId: string | null;
  defaultId: string | null;
  onSelect: (id: string) => void;
}

function Step2({ templates, loading, errorText, selectedId, defaultId, onSelect }: Step2Props) {
  const ordered = useMemo(() => {
    return [...templates].sort((a, b) => {
      if (a.is_default && !b.is_default) return -1;
      if (!a.is_default && b.is_default) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [templates]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-bb-navy">Folder template</h2>
        <p className="text-xs text-slate-500 mt-1">
          The default template is applied automatically. Pick a different one to overlay custom
          folders, or skip to continue with the default.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : errorText ? (
        <div className="text-sm text-red-600">{errorText}</div>
      ) : ordered.length === 0 ? (
        <div className="text-sm text-slate-500">No templates available.</div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ordered.map((t) => {
            const isSelected = t.id === selectedId;
            const isDefault = t.id === defaultId || t.is_default;
            return (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => onSelect(t.id)}
                  className={cn(
                    'w-full text-left border p-4 bg-white transition-colors',
                    isSelected
                      ? 'border-bb-navy ring-1 ring-bb-navy/30'
                      : 'border-slate-200 hover:border-bb-navy/40',
                  )}
                  aria-pressed={isSelected}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">
                        {t.name}
                      </div>
                      {t.description && (
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                          {t.description}
                        </div>
                      )}
                    </div>
                    {isDefault && (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-bb-mint/30 text-bb-navy font-semibold whitespace-nowrap">
                        Default
                      </span>
                    )}
                  </div>
                  <div className="mt-3 text-xs text-slate-600 font-mono whitespace-pre-wrap max-h-28 overflow-hidden">
                    {t.structure && t.structure.length > 0 ? (
                      previewStructure(t.structure, 5)
                    ) : (
                      <span className="text-slate-400">(empty structure)</span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function previewStructure(nodes: FolderTemplateNode[], limit: number): string {
  const out: string[] = [];
  const walk = (list: FolderTemplateNode[], depth: number) => {
    for (const n of list) {
      if (out.length >= limit) return;
      out.push(`${'  '.repeat(depth)}${n.name}`);
      if (n.children && n.children.length) walk(n.children, depth + 1);
    }
  };
  walk(nodes, 0);
  const more = countNodes(nodes) - out.length;
  const base = out.join('\n');
  return more > 0 ? `${base}\n…and ${more} more` : base;
}

function countNodes(nodes: FolderTemplateNode[]): number {
  let total = 0;
  for (const n of nodes) {
    total += 1;
    if (n.children) total += countNodes(n.children);
  }
  return total;
}

// -------------------- Step 3 --------------------

interface Step3Props {
  email: string;
  name: string;
  role: InviteRole;
  onEmailChange: (v: string) => void;
  onNameChange: (v: string) => void;
  onRoleChange: (v: InviteRole) => void;
}

function Step3({ email, name, role, onEmailChange, onNameChange, onRoleChange }: Step3Props) {
  return (
    <div className="max-w-lg space-y-4">
      <div>
        <h2 className="text-base font-semibold text-bb-navy">Primary contact</h2>
        <p className="text-xs text-slate-500 mt-1">
          Optionally invite a client user now. You can always invite more users later.
        </p>
      </div>
      <div>
        <Label htmlFor="onboard-invite-email">Email</Label>
        <Input
          id="onboard-invite-email"
          type="email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          placeholder="owner@acme.com"
        />
      </div>
      <div>
        <Label htmlFor="onboard-invite-name">Full name (optional)</Label>
        <Input
          id="onboard-invite-name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Jane Smith"
        />
      </div>
      <div>
        <Label htmlFor="onboard-invite-role">Role</Label>
        <Select
          id="onboard-invite-role"
          value={role}
          onChange={(v) => onRoleChange(v as InviteRole)}
          options={[
            { value: 'client_admin', label: 'Client admin' },
            { value: 'client_user', label: 'Client user' },
          ]}
          placeholder=""
        />
      </div>
    </div>
  );
}

// -------------------- Step 4 --------------------

interface Step4Props {
  done: boolean;
  orgId: string | null;
  orgName: string;
  orgSlug: string;
  selectedTemplateName: string | null;
  appliedTemplateName: string | null;
  inviteEmail: string;
  inviteSent: boolean;
  inviteToken: string | null;
  onOnboardAnother: () => void;
}

function Step4({
  done,
  orgId,
  orgName,
  orgSlug,
  selectedTemplateName,
  appliedTemplateName,
  inviteEmail,
  inviteSent,
  inviteToken,
  onOnboardAnother,
}: Step4Props) {
  const toast = useToast();
  const acceptUrl = useMemo(() => {
    if (!inviteToken) return null;
    if (typeof window === 'undefined') return `/accept-invite?token=${inviteToken}`;
    return `${window.location.origin}/accept-invite?token=${inviteToken}`;
  }, [inviteToken]);

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Unable to copy ${label}`);
    }
  };

  if (!done) {
    return (
      <div className="max-w-lg space-y-4">
        <div>
          <h2 className="text-base font-semibold text-bb-navy">Review &amp; confirm</h2>
          <p className="text-xs text-slate-500 mt-1">
            Review your choices below. Nothing has been created yet — click <strong>Confirm &amp; create</strong> to proceed.
          </p>
        </div>
        <dl className="space-y-3 text-sm">
          <div className="border border-slate-200 bg-white p-3">
            <dt className="text-xs uppercase tracking-wide text-slate-500">Organization</dt>
            <dd className="mt-1 text-slate-800 font-medium">{orgName || <span className="text-slate-400">—</span>}</dd>
            <dd className="text-xs text-slate-500">{orgSlug || <span className="text-slate-400">—</span>}</dd>
          </div>
          <div className="border border-slate-200 bg-white p-3">
            <dt className="text-xs uppercase tracking-wide text-slate-500">Folder template</dt>
            <dd className="mt-1 text-slate-800 font-medium">
              {selectedTemplateName ?? 'Default (auto-applied)'}
            </dd>
          </div>
          <div className="border border-slate-200 bg-white p-3">
            <dt className="text-xs uppercase tracking-wide text-slate-500">Primary contact</dt>
            <dd className="mt-1 text-slate-800 font-medium">
              {inviteEmail.trim() ? `Invite will be sent to ${inviteEmail}` : 'No invite'}
            </dd>
          </div>
        </dl>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 bg-bb-mint/20 border border-bb-mint p-4">
        <div className="mt-0.5 w-7 h-7 flex items-center justify-center bg-bb-mint text-bb-navy">
          <Check size={16} />
        </div>
        <div>
          <div className="text-sm font-semibold text-bb-navy">Client onboarded successfully</div>
          <div className="text-xs text-slate-700 mt-1">
            {orgName} ({orgSlug}) is ready to go.
          </div>
        </div>
      </div>

      <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div className="border border-slate-200 bg-white p-3">
          <dt className="text-xs uppercase tracking-wide text-slate-500">Organization</dt>
          <dd className="mt-1 text-slate-800 font-medium">{orgName}</dd>
          <dd className="text-xs text-slate-500">{orgSlug}</dd>
        </div>
        <div className="border border-slate-200 bg-white p-3">
          <dt className="text-xs uppercase tracking-wide text-slate-500">Folder template</dt>
          <dd className="mt-1 text-slate-800 font-medium">
            {appliedTemplateName ?? 'Default (auto-applied)'}
          </dd>
        </div>
        <div className="border border-slate-200 bg-white p-3 md:col-span-2">
          <dt className="text-xs uppercase tracking-wide text-slate-500">Primary contact</dt>
          <dd className="mt-1 text-slate-800 font-medium">
            {inviteSent ? `Invite sent to ${inviteEmail}` : 'No invite sent'}
          </dd>
        </div>
      </dl>

      {inviteSent && inviteToken && acceptUrl && (
        <div className="border border-slate-200 bg-slate-50 p-3 text-xs">
          <div className="font-semibold text-slate-700">Share manually if email doesn't arrive</div>
          <div className="mt-2 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-slate-500 whitespace-nowrap">Token:</span>
              <code className="flex-1 bg-white border border-slate-200 px-2 py-1 font-mono break-all">
                {inviteToken}
              </code>
              <button
                type="button"
                onClick={() => copy(inviteToken, 'Token')}
                className="inline-flex items-center gap-1 px-2 py-1 border border-bb-lilac text-bb-navy hover:bg-bb-navy/5"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-500 whitespace-nowrap">Accept URL:</span>
              <code className="flex-1 bg-white border border-slate-200 px-2 py-1 font-mono break-all">
                {acceptUrl}
              </code>
              <button
                type="button"
                onClick={() => copy(acceptUrl, 'URL')}
                className="inline-flex items-center gap-1 px-2 py-1 border border-bb-lilac text-bb-navy hover:bg-bb-navy/5"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-2">
        {orgId && (
          <Link
            to={`/workspace/${orgId}`}
            className="inline-flex items-center gap-2 px-3 py-2 bg-bb-navy text-white font-medium hover:bg-bb-navy/90"
          >
            View workspace
          </Link>
        )}
        <Button variant="secondary" onClick={onOnboardAnother} className="text-sm">
          Onboard another
        </Button>
      </div>
    </div>
  );
}
